import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/Dialog";
import { ApiError, providerApi, type CashDrawerRecord, type ProviderVenue } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { formatEgp } from "../provider-orders/order-status";
import { notifyShiftChanged, useCashShift } from "./useCashShift";

function toCents(value: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : Number.NaN;
}

export function formatShiftTime(iso: string, language: string) {
  return new Date(iso).toLocaleString(language === "ar" ? "ar-EG" : "en-US", {
    weekday: "short", hour: "numeric", minute: "2-digit",
  });
}

/** Plain-words result of a till count: matches, short by, over by. */
export function varianceText(variance: number, language: string, t: (key: string, values?: Record<string, string | number>) => string) {
  if (Math.abs(variance) < 0.005) return { tone: "success" as const, text: t("The till matches.") };
  const amount = formatEgp(Math.abs(variance), language);
  return variance < 0
    ? { tone: "danger" as const, text: t("The till is short by {amount}.", { amount }) }
    : { tone: "warning" as const, text: t("The till is over by {amount}.", { amount }) };
}

interface Props {
  open: boolean;
  onClose: () => void;
  venue: ProviderVenue;
  isOnline: boolean;
}

/**
 * Start or end a cash shift. Starting records the cash already in the till;
 * ending compares what was counted with what should be there.
 */
export function ShiftDialog({ open, onClose, venue, isOnline }: Props) {
  const { t, language } = useAppPreferences();
  const shift = useCashShift(open ? venue.id : null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ended, setEnded] = useState<CashDrawerRecord | null>(null);

  useEffect(() => {
    if (!open) return;
    setAmount("");
    setNote("");
    setError(null);
    setEnded(null);
  }, [open]);

  const cents = toCents(amount);
  const hasAmount = amount.trim() !== "" && Number.isFinite(cents) && cents >= 0;
  const openShift = shift.openShift;
  const variance = openShift && hasAmount ? cents / 100 - shift.expectedCash : null;
  const lastCount = shift.lastShift?.counted_cash_amount ?? null;

  const start = async () => {
    if (!hasAmount) {
      setError(t("Enter the cash in the till. Enter 0 if it's empty."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await providerApi.openCashDrawer({ venue_id: venue.id, opening_cash_amount: cents / 100, note: note.trim() || null });
      notifyShiftChanged();
      onClose();
    } catch (requestError) {
      setError(requestError instanceof ApiError && requestError.status !== 0 ? requestError.detail : t("Could not start the shift. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    if (!openShift) return;
    if (!hasAmount) {
      setError(t("Count the cash in the till and enter the total."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const summary = await providerApi.closeCashDrawer(openShift.id, { counted_cash_amount: cents / 100, note: note.trim() || null });
      setEnded(summary.last_closed_drawer);
      notifyShiftChanged();
    } catch (requestError) {
      setError(requestError instanceof ApiError && requestError.status !== 0 ? requestError.detail : t("Could not end the shift. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  };

  const title = ended ? t("Shift ended") : openShift ? t("End shift") : t("Start shift");

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {ended
            ? venue.name
            : openShift
              ? t("Count the cash in the till. We'll check it against what should be there.")
              : t("A shift keeps track of the cash in the till. It doesn't open or close the venue; that follows your opening hours.")}
        </DialogDescription>

        {shift.error ? <div className="ops-banner" data-tone="danger" role="alert">{shift.error}</div> : null}
        {shift.isLoading && !shift.summary ? <div className="ops-empty py-4">{t("Loading…")}</div> : null}

        {ended ? (
          <div className="grid gap-3">
            <ShiftMath
              counted={ended.counted_cash_amount ?? 0}
              expected={ended.expected_cash_amount}
              language={language}
              starting={ended.opening_cash_amount}
            />
            {ended.variance_amount !== null ? (
              <div className="ops-banner" data-tone={varianceText(ended.variance_amount, language, t).tone} role="status">
                {varianceText(ended.variance_amount, language, t).text}
              </div>
            ) : null}
            <p className="ops-copy text-xs">{t("Start a new shift when the next person takes over the till.")}</p>
            <button className="ops-button" data-variant="primary" onClick={onClose} type="button">{t("Done")}</button>
          </div>
        ) : shift.summary ? (
          <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); void (openShift ? end() : start()); }}>
            {openShift ? (
              <>
                <p className="ops-copy text-sm">{t("Started {time}", { time: formatShiftTime(openShift.opened_at, language) })}</p>
                <ShiftMath
                  counted={variance !== null ? cents / 100 : null}
                  expected={shift.expectedCash}
                  language={language}
                  starting={openShift.opening_cash_amount}
                  taken={shift.cashTaken}
                />
                <label className="grid gap-1">
                  <span className="ops-label">{t("Cash you counted")}</span>
                  <input autoFocus className="ops-input" inputMode="decimal" min="0" onChange={(event) => setAmount(event.target.value)} step="0.01" type="number" value={amount} />
                </label>
                {variance !== null ? (
                  <div className="ops-banner" data-tone={varianceText(variance, language, t).tone} role="status">
                    {varianceText(variance, language, t).text}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <label className="grid gap-1">
                  <span className="ops-label">{t("Cash in the till now")}</span>
                  <input autoFocus className="ops-input" inputMode="decimal" min="0" onChange={(event) => setAmount(event.target.value)} placeholder="0.00" step="0.01" type="number" value={amount} />
                </label>
                {lastCount !== null ? (
                  <button className="ops-button justify-self-start" data-size="sm" data-variant="secondary" onClick={() => setAmount(lastCount.toFixed(2))} type="button">
                    {t("Same as last count ({amount})", { amount: formatEgp(lastCount, language) })}
                  </button>
                ) : null}
              </>
            )}
            <label className="grid gap-1">
              <span className="ops-label">{t("Note (optional)")}</span>
              <input className="ops-input" maxLength={500} onChange={(event) => setNote(event.target.value)} placeholder={openShift ? t("Why it's short or over, who counted…") : t("Who is on the till…")} value={note} />
            </label>
            {error ? <div className="ops-banner" data-tone="danger" role="alert">{error}</div> : null}
            {!isOnline ? <p className="ops-copy text-xs">{t("Shifts need a connection.")}</p> : null}
            <div className="flex flex-wrap justify-end gap-2">
              <button className="ops-button" data-variant="secondary" onClick={onClose} type="button">{t("Cancel")}</button>
              <button className="ops-button" data-variant="primary" disabled={busy || !isOnline} type="submit">
                {busy ? t("Saving…") : openShift ? t("End shift") : t("Start shift")}
              </button>
            </div>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ShiftMath({ starting, taken, expected, counted, language }: {
  starting: number;
  taken?: number;
  expected: number;
  counted: number | null;
  language: string;
}) {
  const { t } = useAppPreferences();
  return (
    <dl className="shift-math">
      <div><dt>{t("Starting cash")}</dt><dd>{formatEgp(starting, language)}</dd></div>
      {taken !== undefined ? <div><dt>{t("+ Cash taken this shift")}</dt><dd>{formatEgp(taken, language)}</dd></div> : null}
      <div data-total="true"><dt>{t("Should be in the till")}</dt><dd>{formatEgp(expected, language)}</dd></div>
      {counted !== null ? <div><dt>{t("You counted")}</dt><dd>{formatEgp(counted, language)}</dd></div> : null}
    </dl>
  );
}
